/**
 * Части формы записи по макету 27.10.2026 (docs/design/op-sheet-2026-10-27).
 *
 * Порядок — как у перевода в банке: сначала, откуда деньги, потом сумма,
 * потом категория. Вид операции — значками в овале, как нижнее меню:
 * выбранный раскрывается со словом. Счёт — карточкой, которую листают
 * пальцем; тап по ней открывает прежний список (`AccountField`), и всё, что
 * в нём было, — группы, суммы, «Новый счёт» — остаётся.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatMoney, type Account } from '@skoka/core';
import { Icon } from './icon.js';
import { AccountGlyph, accountColor } from './account-glyph.js';
import { accountGroups } from './account-groups.js';
import { AccountField, CategoryField, quickCategories } from './operation-fields.js';
import { CategoryGlyph, colorOf, type CategoryColor } from './category-icon.js';
import type { Category } from './categories.js';
import type { CategoryKind } from './category-tree.js';

/** Вид операции — значками в овале; выбранный — графитовой плашкой со словом. */
export function OpTypeSwitch<K extends string>({ types, value, onPick }: {
  types: ReadonlyArray<readonly [K, string, string]>; value: K; onPick: (k: K) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  // Не влезло на узком экране — выбранный прокручивается на виду.
  useEffect(() => {
    box.current?.querySelector<HTMLElement>('.on')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [value]);
  return (
    <div className="optype" ref={box} role="group" aria-label="Вид операции">
      {types.map(([k, label, icon]) => (
        <button key={k} type="button" aria-pressed={value === k} aria-label={label}
          className={`opt${value === k ? ' on' : ''}`} onClick={() => onPick(k)}>
          <Icon name={icon} size={16} />{value === k && <span>{label}</span>}
        </button>
      ))}
    </div>
  );
}

/** Зазор между карточками в карусели, как `.opcard.side` в стилях. */
const GAP = 10;
/** Отпустил дальше — листается; короче — карточка возвращается. */
const STEP = 40;
/** Быстрое смахивание листает и на коротком пути, точек в миллисекунду. */
const FLICK = 0.4;
const calm = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Карусель карточек, как в банке (28.10.2026): палец ведёт ленту, следом
 * въезжает соседняя карточка; отпустил — доезжает на место или возвращается.
 * Тап — список. Точки внизу — сколько вариантов и какой сейчас, при любом
 * их числе; край следующей карточки выглядывает справа.
 *
 * Соседей рисуем только на время жеста: в покое в форме одна карточка.
 */
export function SwipeCard({ tone, index, count, render, onStep, onTap, label }: {
  /** Цвет карточки с номером `i`. */
  tone: (i: number) => CategoryColor;
  index: number; count: number;
  /** Содержимое карточки с номером `i`. */
  render: (i: number) => ReactNode;
  onStep: (dir: 1 | -1) => void; onTap: () => void;
  /** Для диктора: «Списать с: Основная карта». */
  label: string;
}) {
  const track = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; y: number; moved: boolean; last: number; t: number; v: number } | null>(null);
  const [drag, setDrag] = useState(false);
  const move = (x: number, ms = 0) => {
    const el = track.current;
    if (!el) return;
    el.style.transition = ms ? `transform ${ms}ms cubic-bezier(.2,.8,.2,1)` : 'none';
    el.style.transform = x ? `translateX(${x}px)` : '';
  };
  const settle = (dir: 0 | 1 | -1) => {
    const width = track.current?.offsetWidth ?? 0;
    const done = () => { move(0); setDrag(false); if (dir) onStep(dir); };
    if (calm()) { done(); return; }
    move(-dir * (width + GAP), 240);
    window.setTimeout(done, 240);
  };
  const card = (i: number, side?: 'prev' | 'next') => (
    <div key={side ?? 'now'} className={`opcard c-${tone(i)}${count > 1 ? ' more' : ''}${side ? ` side ${side}` : ''}`} aria-hidden={side ? true : undefined}>
      {render(i)}
    </div>
  );
  return (
    <div className={`opcar c-${tone(index)}${count > 1 ? ' more' : ''}`} role="button" tabIndex={0} aria-label={label}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(); }
        if (e.key === 'ArrowRight' && index < count - 1) onStep(1);
        if (e.key === 'ArrowLeft' && index > 0) onStep(-1);
      }}
      onPointerDown={(e) => { start.current = { x: e.clientX, y: e.clientY, moved: false, last: e.clientX, t: e.timeStamp, v: 0 }; }}
      onPointerMove={(e) => {
        const s = start.current;
        if (!s) return;
        const dx = e.clientX - s.x;
        if (!s.moved && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(e.clientY - s.y)) {
          s.moved = true;
          setDrag(true);
          e.currentTarget.setPointerCapture?.(e.pointerId);
        }
        if (!s.moved) return;
        const dt = e.timeStamp - s.t;
        if (dt > 0) s.v = 0.8 * ((e.clientX - s.last) / dt) + 0.2 * s.v;
        s.last = e.clientX; s.t = e.timeStamp;
        // Лента едет за пальцем; у крайней карточки — «резинка».
        const edge = (dx < 0 && index >= count - 1) || (dx > 0 && index <= 0);
        move(edge ? dx * 0.25 : dx);
      }}
      onPointerUp={(e) => {
        const s = start.current;
        start.current = null;
        if (!s) return;
        if (!s.moved) { onTap(); return; }
        const dx = e.clientX - s.x;
        const next = dx < -STEP || (s.v < -FLICK && dx < -8);
        const prev = dx > STEP || (s.v > FLICK && dx > 8);
        settle(next && index < count - 1 ? 1 : prev && index > 0 ? -1 : 0);
      }}
      onPointerCancel={() => { if (start.current?.moved) settle(0); start.current = null; }}>
      <div className="opcar-track" ref={track}>
        {drag && index > 0 && card(index - 1, 'prev')}
        {card(index)}
        {drag && index < count - 1 && card(index + 1, 'next')}
      </div>
      {count > 1 && (
        <span className={`opcard-dots${count > 8 ? ' many' : ''}`} aria-hidden="true">
          {Array.from({ length: count }, (_, i) => <i key={i} className={i === index ? 'on' : ''} />)}
        </span>
      )}
    </div>
  );
}

/** Строка карточки: значок, подпись над названием, справа — сумма. */
export function CardRow({ glyph, caption, name, right, sub }: {
  glyph: ReactNode; caption: string; name: string; right?: string; sub?: string;
}) {
  return (
    <>
      <span className="opcard-row">{glyph}
        <span className="opcard-nm"><small>{caption}</small><b>{name}</b></span>
        {right && <span className="opcard-sum num">{right}</span>}
      </span>
      {sub && <span className="opcard-sub">{sub}</span>}
    </>
  );
}

/** Счёт карточкой: листается в том же порядке, что «Порядок счетов»; тап — прежний список. */
export function AccountCard({ caption, pickerTitle, accounts, value, onChange }: {
  caption: string; pickerTitle: string; accounts: Account[]; value: string; onChange: (id: string) => void;
}) {
  const order = accountGroups(accounts).flatMap((g) => g.accounts);
  const index = Math.max(0, order.findIndex((a) => a.id === value));
  return (
    <AccountField label={caption} pickerTitle={pickerTitle} accounts={accounts} value={value} onChange={onChange}
      trigger={(open, current) => (
        <SwipeCard tone={(i) => { const a = i === index ? current : order[i]; return a ? accountColor(a) : 'gray'; }} index={index} count={order.length}
          label={`${caption}: ${current?.name ?? 'выбрать счёт'}`}
          onTap={open} onStep={(d) => { const next = order[index + d]; if (next) onChange(next.id); }}
          render={(i) => {
            const a = i === index ? current : order[i];
            return a
              ? <CardRow glyph={<AccountGlyph account={a} />} caption={caption} name={a.name} right={formatMoney(a.balance)} />
              : <CardRow glyph={<span className="catico c-gray"><Icon name="credit-card" size={19} /></span>} caption={caption} name="Выбрать счёт" />;
          }} />
      )} />
  );
}

/**
 * Частые категории плитками и «Все» — прежний выбор категории с поиском и
 * деревом. Плитка выбранной — с кольцом. Плитки листаются пальцем вбок, а
 * «Все» стоит справа на месте (28.10.2026).
 */
export function QuickCategories({ categories, facts, kind, value, onChange, onUpdated, isNew = false, label }: {
  categories: Category[]; facts: Array<{ type: string; categoryId?: string; occurredAt?: string }>;
  kind: CategoryKind; value: string; onChange: (id: string) => void; onUpdated: (c: Category[]) => void;
  /** Категория создана автоматически по чеку — метка «новая» под плиткой. */
  isNew?: boolean;
  /** «Категория» или «За что вернули» — для диктора и заголовка списка. */
  label: string;
}) {
  const quick = quickCategories(categories, facts, kind, value, 12);
  const row = useRef<HTMLDivElement>(null);
  // Выбранная — на виду: выбрали голосом или в «Все», а плитка за краем ряда.
  useEffect(() => {
    const box = row.current;
    const on = box?.querySelector<HTMLElement>('.opcat.on');
    if (!box || !on) return;
    const left = on.getBoundingClientRect().left - box.getBoundingClientRect().left + box.scrollLeft;
    if (left < box.scrollLeft || left + on.offsetWidth > box.scrollLeft + box.clientWidth) box.scrollLeft = Math.max(0, left - 8);
  }, [value]);
  return (
    <div className="opcats" role="group" aria-label={label}>
      <div className="opcats-row" ref={row}>
        {quick.map((c) => (
          <button key={c.id} type="button" className={`opcat${c.id === value ? ' on' : ''}`} aria-pressed={c.id === value}
            onClick={() => onChange(c.id)}>
            <CategoryGlyph icon={c.icon} color={colorOf(c)} />
            <span>{c.name}</span>
            {c.id === value && isNew && <em>новая</em>}
          </button>
        ))}
      </div>
      <CategoryField categories={categories} kind={kind} value={value} label={label} isNew={isNew} onUpdated={onUpdated} onChange={onChange}
        trigger={(open) => (
          <button type="button" className="opcat all" onClick={open} aria-label={`${label}: все`}>
            <span className="catico"><Icon name="grid-2" size={17} /></span><span>Все</span>
          </button>
        )} />
    </div>
  );
}
